import { defineStore } from "pinia";
import { ref } from "vue";

import { api, type types } from "@/lib/openapi";
import { liveTimeBody } from "@/stores/live-store";

/**
 * Per-entity "needs attention" counts for the live worktree tree badges.
 *
 * One stek-scoped `get_live_insights` call returns `entity_attention`: a map of
 * entity identity -> { count, worst severity }, already rolled up (a domain /
 * product reflects its subtree, a process its own). The tree reads it per node,
 * the way it reads thread counts. Live mode only — the fetch is driven from the
 * app layout when the live view is active.
 */
export const useLiveInsightsStore = defineStore("liveInsights", () => {
    const countsByEntity = ref<Record<string, types.EntityAttentionCount>>({});
    const loading = ref(false);

    const fetch = async (
        stekId: string | null | undefined,
        stekRelease?: string | null
    ): Promise<void> => {
        if (!stekId) {
            countsByEntity.value = {};
            return;
        }
        loading.value = true;
        try {
            const { data, response } =
                await api.startStekLiveMgtGetLiveInsightsStartPost({
                    body: {
                        stek_id: stekId,
                        ...liveTimeBody(),
                        stek_release: stekRelease ?? null,
                    },
                });
            if (response.ok && data) {
                countsByEntity.value = data.entity_attention ?? {};
            }
        } catch {
            // Best-effort: the tree just shows no attention badges on failure.
        } finally {
            loading.value = false;
        }
    };

    const attentionForEntity = (
        entityId: string
    ): types.EntityAttentionCount | undefined => countsByEntity.value[entityId];

    const clear = (): void => {
        countsByEntity.value = {};
    };

    return { countsByEntity, loading, fetch, attentionForEntity, clear };
});
